import { ImagePaths } from '../../lib/imageUrls.js';

export const aiForFamilies = {

    id: 10,
    slug: 'ai-for-families',
    ogImage: '/og/courses/ai-for-families.jpg',
    courseCode: 'AI-FAM-001',
    title: 'AI Navigator: Safe & Smart AI for Families',
    subtitle: 'Navigate the AI landscape with confidence and care',
    valueProposition: 'Empower parents and students with essential knowledge to safely navigate generative AI, understand duty of care responsibilities, and prepare for approved AI use in education.',
    duration: '12 hours',
    students: 'New',
    level: 'Foundational',
    image: ImagePaths.COURSE_1,
    shortDescription: 'AI-FAM-001 — Family AI safety training covering privacy, consent, and online safety for parents, students, and school staff.',
    price: {
      aud: 199,
      currency: 'AUD',
      earlyBird: 150
    },
    stripeConfig: {
    priceId: 'price_1T9bE2Ku1nyc322UQTrAerHg',
    productId: 'prod_U7qvMNApe3XiJU'
  },
    
    snapshot: {
      whoItsFor: [
        'Parents wanting to guide their children\'s AI use',
        'K-12 students (with parental guidance)',
        'School staff (non-IT) supporting student wellbeing',
        'Career changers and return-to-workforce professionals',
        'University students entering the AI-enabled workforce'
      ],
      accreditation: 'Aligned to Australian Privacy Principles and eSafety Commissioner guidelines'
    },
    
    logistics: {
      classSize: 'Maximum 25 participants for interactive discussion',
      requirements: [
        'Computer or tablet with internet access',
        'Reliable internet connection (minimum 5 Mbps)',
        'No prior technical experience required',
        'Parental supervision recommended for K-12 students'
      ]
    },
    
    overview: 'This foundational course demystifies generative AI for families navigating the rapidly changing technology landscape. Parents and students will learn what GenAI is, understand why schools and organisations take a cautious "duty of care" approach, and prepare confidently for safe, approved AI use. Covering privacy, consent, digital citizenship, and online safety, this course provides practical guidance grounded in Australian frameworks including the Privacy Act, Australian Privacy Principles, and eSafety Commissioner guidelines.',
    
    outcomes: [
      'Explain what generative AI is and how it differs from traditional technology',
      'Identify privacy risks and consent considerations when using AI tools',
      'Apply the "duty of care" principles that guide schools\' AI policies',
      'Recognise online safety concerns and protective strategies for AI interactions',
      'Evaluate AI-generated content for accuracy, bias, and appropriateness',
      'Demonstrate responsible digital citizenship in AI-enabled environments',
      'Support children and students in safe, ethical AI use',
      'Prepare for approved AI use in educational and workplace settings'
    ],
    
    audience: [
      'Parents and guardians of school-aged children',
      'K-12 students (Years 5-12 with parental guidance)',
      'School staff including teachers, counsellors, and administrators',
      'Career changers entering technology-adjacent fields',
      'Return-to-workforce professionals updating their skills',
      'University students preparing for AI-enabled workplaces'
    ],
    
    prerequisites: [
      'Basic familiarity with computers and internet use',
      'No technical or programming experience required',
      'Open mindset about technology and willingness to learn',
      'For K-12 students: parental or guardian involvement recommended'
    ],
    
    modules: [
      {
        title: 'Understanding Generative AI',
        duration: '3 hours',
        topics: [
          'What is generative AI and how does it work?',
          'Common AI tools and applications in daily life',
          'How AI learns and creates content',
          'Differences between AI, automation, and human intelligence',
          'Real-world examples of AI benefits and limitations'
        ]
      },
      {
        title: 'Privacy, Consent, and Data Protection',
        duration: '2.5 hours',
        topics: [
          'Australian Privacy Principles (APPs) and AI',
          'What happens to your data when using AI tools',
          'Understanding terms of service and privacy policies',
          'Protecting personal information and student data',
          'Consent considerations for minors and families'
        ]
      },
      {
        title: 'Duty of Care and School AI Policies',
        duration: '2 hours',
        topics: [
          'Why schools take a cautious approach to AI',
          'Legal and ethical duty of care responsibilities',
          'Understanding your school\'s AI acceptable use policy',
          'Age-appropriate AI use and restrictions',
          'Supporting compliance while enabling learning'
        ]
      },
      {
        title: 'Online Safety and Critical Thinking',
        duration: '2.5 hours',
        topics: [
          'eSafety Commissioner guidelines for AI use',
          'Identifying misleading or inappropriate AI content',
          'Cyberbullying and AI-generated harassment',
          'Protecting against AI-powered scams and deepfakes',
          'Teaching critical evaluation of AI outputs'
        ]
      },
      {
        title: 'Responsible and Ethical AI Use',
        duration: '1.5 hours',
        topics: [
          'Digital citizenship in the AI age',
          'Academic integrity and AI assistance',
          'Bias, fairness, and inclusivity in AI',
          'Intellectual property and copyright considerations',
          'Building healthy AI habits'
        ]
      },
      {
        title: 'Preparing for Approved AI Use',
        duration: '0.5 hours',
        topics: [
          'What "approved AI use" looks like in schools and workplaces',
          'Tools and practices for safe AI exploration',
          'Continuing the conversation with family and educators',
          'Resources for staying informed as AI evolves'
        ]
      }
    ],
    
    format: {
      type: 'Live online training',
      includes: [
        '12 hours of interactive instruction',
        'Real-world scenarios and family discussion guides',
        'Practical worksheets and safety checklists',
        'Access to recorded sessions for 3 months',
        'Parent-student conversation starters',
        'Ongoing resource library and updates',
        'Certificate of completion',
        'Q&A sessions with education and safety experts'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Six-week program (2 hours per week) with flexible evening sessions for working parents'
    },
    
    // Pathway relationships
    pathways: [
      'ai-foundations-professional',
      'family-ai-literacy'
    ],

    skillsTaught: [
      'AI safety and digital citizenship',
      'Privacy and consent for AI use',
      'Responsible AI for families',
      'Navigating school AI policies',
      'AI literacy for parents and students'
    ],

    hasCapstone: false,

    nextCourses: [
      'ai-for-business'
    ],
    
    faqs: [
      {
        question: 'Is this course suitable for both parents and students?',
        answer: 'Yes! The course is designed for parents, students (Years 5-12), and educators to learn together. We encourage families to participate jointly to build shared understanding and open communication about AI. For younger students, parental involvement is essential.'
      },
      {
        question: 'Do I need any technical background to take this course?',
        answer: 'No technical experience is required. This course is designed for everyday users who want to understand AI from a safety, privacy, and practical perspective. We explain concepts in plain language with real-world examples.'
      },
      {
        question: 'How does this course relate to my child\'s school AI policies?',
        answer: 'We cover the general principles behind school AI policies including duty of care, privacy, and age-appropriate use. While we can\'t address every school\'s specific policy, you\'ll understand the "why" behind restrictions and be better equipped to discuss AI use with your child\'s school.'
      },
      {
        question: 'Will this course teach my child how to use AI tools?',
        answer: 'This is a safety and readiness course, not a hands-on AI skills course. We focus on understanding what AI is, recognising risks, and building judgement for responsible use. We don\'t provide access to AI tools or teach prompting techniques—that comes later with approved, supervised use.'
      },
      {
        question: 'What Australian frameworks and guidelines does the course cover?',
        answer: 'The course is grounded in Australian context including the Privacy Act, Australian Privacy Principles (APPs), eSafety Commissioner guidelines, and duty of care principles relevant to schools and organisations. All examples and case studies reflect Australian law and educational settings.'
      },
      {
        question: 'Can schools book this course for parent-teacher sessions?',
        answer: 'Absolutely! We offer customised delivery for schools wanting to educate parents, students, and staff together. This can be delivered as parent information evenings, professional development for non-IT staff, or integrated into student wellbeing programs. Contact us for a private quote.'
      },
      {
        question: 'How is this different from IT or cybersecurity training?',
        answer: 'This course focuses on everyday AI safety, not technical security implementation. It\'s for families, students, and non-technical school staff who need to understand AI from a care, consent, and online safety perspective—not network administrators or IT professionals.'
      },
      {
        question: 'What happens after the course—can my child start using AI?',
        answer: 'This course prepares families to have informed conversations about AI and understand safety principles. Whether and how your child uses AI depends on their age, school policies, and your family values. You\'ll leave equipped to make those decisions confidently and guide approved use responsibly.'
      }
    ]
};
